import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { errorToast, successToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";
import {
  Field,
  inputStyle,
  hintStyle,
  warnStyle,
  primaryBtnStyle,
  secondaryBtnStyle,
  withDisabled,
} from "../common/FormKit";
import PillTabs from "../common/PillTabs";
import { adjustLoyaltyCustomer } from "../../utils/apiCalls";

/**
 * Manually add or deduct stamps, rewards or points on one customer.
 *
 * Mirrors AdjustWalletModal on purpose: one requestId per OPEN (so a double-click or a retry after
 * a network error is a no-op server-side), the amount is always positive and the direction decides
 * the sign, a live "result" line, and the form locks after success. The server enforces every rule
 * shown here again; the checks in this file only tell the operator before the request.
 */

// Cap for ONE adjustment, per kind. Mirrors ADJUST_MAX_AMOUNTS in the API's src/const/loyalty.js —
// change both together, or the form allows an amount the server then rejects.
export const MAX_AMOUNTS = { stamps: 50, rewards: 50, points: 1000 };

const KIND_LABELS = { stamps: "Stamps", rewards: "Rewards", points: "Points" };

// Which kinds a programme can adjust. Stamps programmes have stamps and rewards; percentage
// programmes earn points.
export const kindsFor = (loyaltyType) => (loyaltyType === "stamps" ? ["stamps", "rewards"] : ["points"]);

const ADD_REASONS = [
  "Service recovery — order issue",
  "Goodwill gesture",
  "Missed at the till",
  "Paper card migration",
  "Manual correction",
];
const DEDUCT_REASONS = [
  "Reversing an adjustment made in error",
  "Manual correction",
  "Fraud investigation",
];

const newRequestId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `adj-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Current available balance per kind — what a deduction is limited by. */
const availableOf = (kind, balances) => {
  if (kind === "stamps") return Number(balances?.stampsBalance) || 0;
  if (kind === "rewards") return Number(balances?.rewardsAvailable) || 0;
  return Number(balances?.rewardPoints) || 0;
};

/**
 * The "result" preview. Adding stamps across the threshold converts them into rewards right
 * away, exactly as an order would, so the preview shows that too.
 */
export const previewAdjustment = ({ kind, isAdd, amount, balances, rewardOnStampsCount }) => {
  const current = availableOf(kind, balances);
  const raw = current + (isAdd ? amount : -amount);
  const lines = [{ label: KIND_LABELS[kind], from: current, to: raw }];

  const threshold = Number(rewardOnStampsCount) || 0;
  if (kind === "stamps" && isAdd && threshold > 0 && raw >= threshold) {
    const rewards = Math.floor(raw / threshold);
    const rewardsNow = Number(balances?.rewardsAvailable) || 0;
    lines[0].to = raw % threshold;
    lines.push({ label: "Rewards", from: rewardsNow, to: rewardsNow + rewards });
    return { lines, rewardsIssued: rewards };
  }
  return { lines, rewardsIssued: 0 };
};

const Direction = ({ value, onChange, disabled }) => (
  <div style={{ display: "flex", gap: 8 }}>
    {[
      { key: "add", label: "Add", color: C.green, bg: C.greenLight },
      { key: "deduct", label: "Deduct", color: C.orange, bg: C.orangeLight },
    ].map((d) => (
      <button
        key={d.key}
        type="button"
        aria-pressed={value === d.key}
        onClick={() => onChange(d.key)}
        disabled={disabled}
        style={{
          flex: 1, padding: "9px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          border: `1.5px solid ${value === d.key ? d.color : C.border}`,
          background: value === d.key ? d.bg : C.white,
          color: value === d.key ? d.color : C.textMed,
        }}
      >
        {d.label}
      </button>
    ))}
  </div>
);

const ContextStrip = ({ customer, loyaltyType, balances }) => {
  const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") || "—";
  const cells = loyaltyType === "stamps"
    ? [["Stamps", balances?.stampsBalance], ["Rewards", balances?.rewardsAvailable]]
    : [["Points", balances?.rewardPoints]];
  return (
    <div style={{ background: C.bg, borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 160px", minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: 0.5 }}>CUSTOMER</div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{name}</div>
        <div style={{ fontSize: 12, color: C.textMed }}>{customer?.phoneNo || "—"}</div>
      </div>
      {cells.map(([label, value]) => (
        <div key={label} style={{ minWidth: 70 }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: 0.5 }}>{label.toUpperCase()}</div>
          <div style={{ fontSize: 18, color: C.text, fontWeight: 800 }}>{Number(value) || 0}</div>
        </div>
      ))}
    </div>
  );
};

const AdjustLoyaltyModal = ({ isOpen, toggle, merchantId, customer, loyalty, balances, onDone }) => {
  const kinds = kindsFor(loyalty?.loyaltyType);
  const [kind, setKind] = useState(kinds[0]);
  const [direction, setDirection] = useState("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);

  // Fresh state and a fresh requestId per OPEN. A new id per click would defeat idempotency; one id
  // across opens would make a genuine second adjustment silently vanish.
  useEffect(() => {
    if (isOpen) {
      setRequestId(newRequestId());
      setKind(kindsFor(loyalty?.loyaltyType)[0]);
      setDirection("add");
      setAmount("");
      setReason("");
      setSubmitting(false);
      setSubmitted(false);
    }
  }, [isOpen, loyalty?.loyaltyType]);

  const value = Number(amount);
  const maxAmount = MAX_AMOUNTS[kind];
  const validAmount = Number.isInteger(value) && value >= 1 && value <= maxAmount;
  const isAdd = direction === "add";
  const available = availableOf(kind, balances);

  const errors = useMemo(() => {
    const e = [];
    if (!validAmount) e.push(`Enter a whole number between 1 and ${maxAmount}.`);
    if (reason.trim().length < 3) e.push("A reason is required (at least 3 characters).");
    if (!isAdd && validAmount && value > available) {
      e.push(`Not enough ${kind} — the customer has ${available}.`);
    }
    return e;
  }, [validAmount, maxAmount, value, reason, isAdd, available, kind]);

  const preview = validAmount
    ? previewAdjustment({ kind, isAdd, amount: value, balances, rewardOnStampsCount: loyalty?.rewardOnStampsCount })
    : null;

  const submit = async () => {
    if (errors.length || submitting || submitted) return;
    setSubmitting(true);
    const result = await adjustLoyaltyCustomer(customer.id, {
      merchantId,
      kind,
      direction,
      amount: value,
      reason: reason.trim(),
      requestId,
    });
    setSubmitting(false);

    if (!result.ok) {
      // Left unlocked so the operator can correct and retry with the SAME requestId.
      errorToast(result.message || "The adjustment was not applied");
      return;
    }

    setSubmitted(true);
    if (result.data?.alreadyApplied) {
      successToast("Already applied — this adjustment had gone through already");
    } else if (result.data?.rewardsIssued) {
      successToast(`${KIND_LABELS[kind]} added — ${result.data.rewardsIssued} reward(s) issued`);
    } else {
      successToast(`${KIND_LABELS[kind]} ${isAdd ? "added" : "deducted"}`);
    }
    if (onDone) onDone();
  };

  const locked = submitting || submitted;
  const canSubmit = !locked && errors.length === 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Adjust loyalty</ModalHeader>
      <ModalBody>
        <ContextStrip customer={customer} loyaltyType={loyalty?.loyaltyType} balances={balances} />

        {kinds.length > 1 && (
          <PillTabs
            ariaLabel="What to adjust"
            tabs={kinds.map((k) => ({ key: k, label: KIND_LABELS[k] }))}
            value={kind}
            onChange={(k) => !locked && setKind(k)}
          />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Direction">
            <Direction value={direction} onChange={setDirection} disabled={locked} />
          </Field>

          <Field
            label={`${KIND_LABELS[kind]} to ${isAdd ? "add" : "deduct"}`}
            required
            hint={
              `Up to ${maxAmount} per adjustment.` +
              (kind === "points" ? " Lifetime points change too, so the customer's tier can move." : "")
            }
          >
            <input
              type="number"
              min="1"
              max={maxAmount}
              step="1"
              inputMode="numeric"
              aria-label="Amount"
              value={amount}
              disabled={locked}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              style={inputStyle}
            />
          </Field>

          <Field label="Reason" required hint="Saved on the loyalty ledger and the audit log.">
            <select
              aria-label="Common reason"
              value=""
              disabled={locked}
              onChange={(e) => e.target.value && setReason(e.target.value)}
              style={{ ...inputStyle, marginBottom: 8 }}
            >
              <option value="">Choose a common reason…</option>
              {(isAdd ? ADD_REASONS : DEDUCT_REASONS).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <textarea
              aria-label="Reason"
              rows={2}
              value={reason}
              disabled={locked}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this adjustment being made?"
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </Field>

          {preview && (
            <div
              data-testid="adjust-preview"
              style={{ background: isAdd ? C.greenLight : C.orangeLight, borderRadius: 10, padding: 12 }}
            >
              <div style={{ fontSize: 11, color: C.textMed, fontWeight: 700, letterSpacing: 0.5 }}>RESULT</div>
              {preview.lines.map((l) => (
                <div key={l.label} style={{ fontSize: 14, color: C.text, marginTop: 4 }}>
                  {l.label} {l.from} → <strong>{Math.max(l.to, 0)}</strong>
                </div>
              ))}
              {preview.rewardsIssued > 0 && (
                <span style={hintStyle}>
                  Crosses {loyalty.rewardOnStampsCount} stamps: {preview.rewardsIssued} reward(s) will be issued right away.
                </span>
              )}
            </div>
          )}

          {errors.length > 0 && !submitted && (amount !== "" || reason !== "") && (
            <div role="alert">
              {errors.map((e) => <span key={e} style={warnStyle}>• {e}</span>)}
            </div>
          )}

          {submitted && (
            <div style={{ padding: 10, background: C.greenLight, borderRadius: 8, fontSize: 13, color: C.text }}>
              Adjustment applied. Close this window to make another.
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" onClick={toggle} style={secondaryBtnStyle}>
          {submitted ? "Close" : "Cancel"}
        </button>
        <button type="button" onClick={submit} disabled={!canSubmit} style={withDisabled(primaryBtnStyle, !canSubmit)}>
          {submitting
            ? "Applying…"
            : `${isAdd ? "Add" : "Deduct"} ${validAmount ? value : ""} ${KIND_LABELS[kind].toLowerCase()}`.replace(/\s+/g, " ")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

Direction.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };
ContextStrip.propTypes = { customer: PropTypes.object, loyaltyType: PropTypes.string, balances: PropTypes.object };
AdjustLoyaltyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** { id, firstName, lastName, phoneNo } */
  customer: PropTypes.object.isRequired,
  /** { loyaltyType: "stamps"|"percentage", rewardOnStampsCount } — the ACTIVE programme */
  loyalty: PropTypes.object.isRequired,
  /** { stampsBalance, rewardsAvailable, rewardPoints } */
  balances: PropTypes.object,
  onDone: PropTypes.func,
};

export default AdjustLoyaltyModal;
